import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SwatchBook } from '../../components/icons';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import Breadcrumb from '../../components/Breadcrumb';
import { COLOR_CATALOG } from '../../data/colorCatalog';
import { COLOR_COMPARISONS } from '../../data/colorComparisons';
import { rgbToHsl } from '../../utils/colors';

type ColorGroup = 'all' | 'warm' | 'cool' | 'neutral';
const GROUPS: { value: ColorGroup; label: string }[] = [
  { value: 'all', label: 'All colors' },
  { value: 'warm', label: 'Warm' },
  { value: 'cool', label: 'Cool' },
  { value: 'neutral', label: 'Neutral' },
];

const DIRECTORY_COLORS = COLOR_CATALOG.map(color => {
  const hsl = rgbToHsl(...color.rgb);
  const group: ColorGroup = hsl.s <= 10 ? 'neutral' : hsl.h < 90 || hsl.h >= 300 ? 'warm' : 'cool';
  return { ...color, group, searchText: [color.name, color.hex, ...color.aliases].join(' ').toLowerCase() };
});

export default function ColorDirectoryPage() {
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState<ColorGroup>('all');
  const query = search.trim().replace(/^#/, '').toLowerCase();
  const filtered = DIRECTORY_COLORS.filter(color => (group === 'all' || color.group === group) && color.searchText.includes(query));

  return (
    <Layout maxWidth="max-w-6xl">
      <Breadcrumb items={[{ label: 'Color names and HEX codes' }]} />
      <PageHeader
        title="Color names and HEX codes"
        icon={SwatchBook}
        eyebrow={`${COLOR_CATALOG.length} digital color references`}
        description="Find color names, HEX codes and matching palettes. US gray and UK grey use the same colour values."
      />

      <section className="ui-panel p-5 sm:p-6" aria-labelledby="directory-search-heading">
        <h2 id="directory-search-heading" className="text-lg font-bold">Find a color</h2>
        <label htmlFor="color-directory-search" className="ui-label mt-4">Color name or HEX code</label>
        <input
          id="color-directory-search"
          className="ui-input w-full"
          type="search"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Try grey or #008080"
          autoComplete="off"
          spellCheck={false}
          aria-describedby="color-directory-status"
        />
        <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Filter colors by hue family">
          {GROUPS.map(item => (
            <button
              type="button"
              key={item.value}
              className={group === item.value ? 'ui-button-primary' : 'ui-button-secondary'}
              aria-pressed={group === item.value}
              onClick={() => setGroup(item.value)}
            >{item.label}</button>
          ))}
        </div>
        <details className="mt-4 text-sm leading-relaxed text-ink-secondary"><summary className="cursor-pointer font-medium">About these color groups</summary><p className="mt-2">Browsing groups use rounded HSL values: saturation up to 10% is neutral; other hues below 90° or from 300° are warm, and the remainder are cool. These are browsing categories, not measurements of physical temperature.</p></details>
      </section>

      <p id="color-directory-status" className="my-5 text-sm text-ink-secondary" role="status" aria-live="polite">Showing {filtered.length} of {COLOR_CATALOG.length} colors.</p>
      {filtered.length ? (
        <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" aria-label="Named color references">
          {filtered.map(color => (
            <li key={color.hex}>
              <Link className="ui-card-link h-full overflow-hidden" to={`/color/${color.hex}`}>
                <div className="h-24 sm:h-28 border-b border-surface-border" style={{ backgroundColor: `#${color.hex}` }} aria-hidden="true" />
                <div className="p-4">
                  <h2 className="font-semibold text-ink">{color.name}</h2>
                  <p className="mt-1 font-mono text-sm text-ink-secondary">#{color.hex}</p>
                  <p className="mt-2 text-xs leading-relaxed text-ink-secondary">{color.cssNames.length ? `CSS: ${color.cssNames.join(', ')}` : 'Descriptive digital reference'}</p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="ui-panel p-6">
          <h2 className="text-lg font-bold">No matching colors</h2>
          <p className="mt-2 text-ink-secondary">Try a shorter name, a six-digit HEX code, or another hue group.</p>
          <button type="button" className="ui-button-secondary mt-4" onClick={() => { setSearch(''); setGroup('all'); }}>Show all colors</button>
        </div>
      )}

      <section className="ui-panel mt-9 p-5 sm:p-6" aria-labelledby="color-naming-heading">
        <h2 id="color-naming-heading" className="text-xl font-bold">Which color names have standard HEX values?</h2>
        <p className="mt-3 leading-relaxed text-ink-secondary">CSS keywords have defined sRGB values: aqua and cyan both mean #00FFFF, while gray and grey both mean #808080. Other names, including sage green and burgundy, identify this catalog's chosen swatches. They do not specify a universal paint, textile, or printing color.</p>
        <p className="mt-3 leading-relaxed text-ink-secondary">This is a selected catalog, not the complete CSS named-color list. Each reference explains its naming basis. Our original turquoise #30D5C8 and CSS turquoise #40E0D0 appear separately because they are different colors.</p>
        <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3 text-sm font-medium">
          <a className="text-brand-700 underline underline-offset-4" href="https://www.w3.org/TR/css-color-4/#named-colors">W3C CSS named-color definitions</a>
          <Link className="text-brand-700 underline underline-offset-4" to="/methodology">How Color Match calculates values</Link>
          <Link className="text-brand-700 underline underline-offset-4" to="/color-palettes">Explore color palette ideas</Link>
        </div>
      </section>

      <nav className="mt-9" aria-label="Color comparison guides">
        <h2 className="text-xl font-bold">Compare similar color names</h2>
        <ul className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {COLOR_COMPARISONS.map(comparison => (
            <li key={comparison.slug}>
              <Link className="ui-card-link h-full p-4 font-medium text-brand-700" to={`/compare/${comparison.slug}`}>{comparison.first.name} vs {comparison.second.name}</Link>
            </li>
          ))}
        </ul>
      </nav>
    </Layout>
  );
}
